/**
 * Public Booking Page — customer-facing self-serve booking form.
 * URL: /book/:tenantId
 * No auth required. Scans a QR / opens a shared link → picks service →
 * picks slot → fills form → submits.
 *
 * On success, if customer wasn't already in our platform, we invite them
 * to create a Konnect account (optional) so they can manage bookings
 * from their own dashboard later.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, Clock, Check, ArrowRight, ArrowLeft, Loader2, Sparkles, User, Mail, Phone,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicBookingPage() {
  const { tenantId } = useParams();
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState(null);
  const [services, setServices] = useState([]);
  const [fields, setFields] = useState([]);
  const [chosenService, setChosenService] = useState(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [slotsByResource, setSlotsByResource] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [chosenSlot, setChosenSlot] = useState(null);
  const [contact, setContact] = useState({ contact_name: '', email: '', phone: '' });
  const [customFields, setCustomFields] = useState({});
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [svcs, flds] = await Promise.all([
          axios.get(`${API}/api/booking/public/${tenantId}/services`),
          axios.get(`${API}/api/booking/public/${tenantId}/fields`).catch(() => ({ data: [] })),
        ]);
        setProfile(svcs.data.profile);
        setServices(svcs.data.services || []);
        setFields(flds.data || []);
      } catch {
        setProfile(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId]);

  useEffect(() => {
    if (step !== 2 || !chosenService) return;
    setLoadingSlots(true);
    axios
      .get(`${API}/api/booking/public/${tenantId}/availability?date=${date}&service_id=${chosenService.id}`)
      .then((r) => setSlotsByResource(r.data.resources || []))
      .catch(() => setSlotsByResource([]))
      .finally(() => setLoadingSlots(false));
  }, [step, chosenService, date, tenantId]);

  const businessName = profile?.business_name || profile?.industry_label || 'Book an Appointment';
  const hasAnySlots = useMemo(
    () => slotsByResource.some((r) => (r.slots || []).length > 0),
    [slotsByResource]
  );

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        service_id: chosenService.id,
        resource_id: chosenSlot.resource_id,
        start_at: chosenSlot.start,
        contact,
        notes,
        custom_fields: customFields,
      };
      const { data } = await axios.post(`${API}/api/booking/public/${tenantId}/book`, payload);
      setBookingResult(data);
      setStep(5);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Booking failed — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-brand" />
      </div>
    );
  }
  if (!profile) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <p className="text-gray-medium">This booking link is invalid or the business hasn&apos;t set up bookings yet.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-4 sm:p-6" data-testid="public-booking">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white shadow-sm text-xs text-gray-medium mb-3">
            <Sparkles className="h-3.5 w-3.5 text-emerald-brand" />
            Powered by Konnect
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-text-primary">{businessName}</h1>
          <p className="text-sm text-gray-medium mt-1">Book online in less than a minute</p>
        </div>

        {/* Step indicator */}
        {step < 5 && (
          <div className="flex items-center gap-2 mb-6">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className={`h-1.5 flex-1 rounded-full ${n <= step ? 'bg-emerald-brand' : 'bg-gray-200'}`} />
            ))}
          </div>
        )}

        <Card>
          <CardContent className="p-6 space-y-4">
            {/* Step 1 — Pick service */}
            {step === 1 && (
              <>
                <h2 className="text-lg font-semibold mb-2">Choose a service</h2>
                {services.length === 0 ? (
                  <p className="text-sm text-gray-medium">No services available for booking yet.</p>
                ) : (
                  <div className="space-y-2">
                    {services.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => { setChosenService(s); setStep(2); }}
                        className="w-full text-left p-4 border rounded-lg hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors flex items-center justify-between"
                        data-testid={`public-service-${s.id}`}
                      >
                        <div>
                          <div className="font-medium text-text-primary">{s.name}</div>
                          <div className="text-xs text-gray-medium mt-1 flex items-center gap-3 flex-wrap">
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{s.duration_min} min</span>
                            {(s.price_min > 0 || s.price_max > 0) && (
                              <span>from ${s.price_min}</span>
                            )}
                          </div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Step 2 — Pick date+slot */}
            {step === 2 && (
              <>
                <h2 className="text-lg font-semibold mb-2">Pick a time · {chosenService?.name}</h2>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} data-testid="public-date" />
                </div>
                {loadingSlots ? (
                  <div className="flex items-center gap-2 py-6 text-gray-medium"><Loader2 className="h-4 w-4 animate-spin" /> Loading slots…</div>
                ) : !hasAnySlots ? (
                  <p className="text-sm text-gray-medium py-4">No open slots on this date — please pick another day.</p>
                ) : (
                  <div className="space-y-3">
                    {slotsByResource.map((r) => (
                      (r.slots || []).length > 0 && (
                        <div key={r.resource_id}>
                          <div className="text-xs font-medium text-gray-500 mb-1">{r.resource_label}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {r.slots.map((slot) => {
                              const t = new Date(slot.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                              const active = chosenSlot?.start === slot.start && chosenSlot?.resource_id === r.resource_id;
                              return (
                                <button
                                  key={slot.start}
                                  className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${active ? 'bg-emerald-brand text-white border-emerald-brand' : 'bg-white border-gray-200 hover:border-emerald-400'}`}
                                  onClick={() => setChosenSlot({ ...slot, resource_id: r.resource_id })}
                                  data-testid={`public-slot-${r.resource_id}-${slot.start}`}
                                >{t}</button>
                              );
                            })}
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Step 3 — Contact */}
            {step === 3 && (
              <>
                <h2 className="text-lg font-semibold mb-2">Your details</h2>
                <div><Label>Full Name *</Label><Input value={contact.contact_name} onChange={(e) => setContact({ ...contact, contact_name: e.target.value })} data-testid="public-name" /></div>
                <div><Label>Email *</Label><Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="public-email" /></div>
                <div><Label>Phone *</Label><Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="public-phone" /></div>
                <p className="text-xs text-gray-medium">We&apos;ll send your confirmation and reminders to these contacts.</p>
              </>
            )}

            {/* Step 4 — Custom intake fields */}
            {step === 4 && (
              <>
                <h2 className="text-lg font-semibold mb-2">A few more details</h2>
                {fields.length === 0 ? (
                  <p className="text-sm text-gray-medium">No extra questions — you&apos;re ready to book.</p>
                ) : fields.map((f) => (
                  <PublicFieldInput key={f.id || f.key} field={f} value={customFields[f.key]} onChange={(v) => setCustomFields({ ...customFields, [f.key]: v })} />
                ))}
                <div><Label>Anything else we should know? (optional)</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="public-notes" /></div>
              </>
            )}

            {/* Step 5 — Confirmation */}
            {step === 5 && bookingResult && (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-emerald-brand" />
                </div>
                <h2 className="text-2xl font-bold text-text-primary mb-2">Booking confirmed!</h2>
                <p className="text-gray-medium text-sm">
                  {chosenService?.name} on <strong>{new Date(bookingResult.start_at).toLocaleString([], { dateStyle: 'full', timeStyle: 'short' })}</strong>
                </p>
                <p className="text-gray-medium text-xs mt-1">Confirmation email sent to {contact.email}</p>

                <div className="mt-6 p-4 border rounded-lg bg-emerald-50/50">
                  <p className="text-sm text-text-primary font-medium">Want to manage this booking?</p>
                  <p className="text-xs text-gray-medium mt-1 mb-3">Create a free Konnect account to see history and reschedule anytime.</p>
                  <Link to="/signup">
                    <Button size="sm" className="bg-emerald-brand" data-testid="public-signup-cta">Create account</Button>
                  </Link>
                </div>
              </div>
            )}
          </CardContent>

          {/* Step nav */}
          {step > 1 && step < 5 && (
            <div className="border-t p-4 flex items-center justify-between bg-gray-50">
              <Button variant="ghost" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              {step < 4 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={
                    (step === 2 && !chosenSlot) ||
                    (step === 3 && !(contact.contact_name && contact.email))
                  }
                  className="bg-emerald-brand"
                  data-testid="public-next-btn"
                >Next <ArrowRight className="h-4 w-4 ml-1" /></Button>
              ) : (
                <Button onClick={submit} disabled={submitting} className="bg-emerald-brand" data-testid="public-submit-btn">
                  {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                  Confirm Booking
                </Button>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}


function PublicFieldInput({ field, value, onChange }) {
  const label = <Label>{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>;
  const tid = `public-field-${field.key}`;
  if (field.type === 'textarea') return <div>{label}<Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={tid} /></div>;
  if (field.type === 'number') return <div>{label}<Input type="number" value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={tid} /></div>;
  if (field.type === 'date') return <div>{label}<Input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={tid} /></div>;
  if (field.type === 'boolean') return <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={tid} />{field.label}</label>;
  if (field.type === 'select') return (
    <div>{label}
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger data-testid={tid}><SelectValue placeholder="Choose…" /></SelectTrigger>
        <SelectContent>{(field.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
  if (field.type === 'multiselect') {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    return (
      <div>{label}
        <div className="flex flex-wrap gap-1.5 mt-1">
          {(field.options || []).map((o) => (
            <button key={o} type="button" onClick={() => toggle(o)} className={`text-xs px-3 py-1 rounded-full border ${arr.includes(o) ? 'bg-emerald-brand text-white border-emerald-brand' : 'bg-white border-gray-200'}`}>{o}</button>
          ))}
        </div>
      </div>
    );
  }
  return <div>{label}<Input value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={tid} /></div>;
}
